import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FiArrowRight, FiHeart, FiSearch, FiX } from 'react-icons/fi';
import { Link, useSearchParams } from 'react-router-dom';
import { Field } from '../../components/a11y';
import { useAuthStore } from '../../stores/auth.store';
import { getShareBaseUrl } from '../../utils/share-url';
import LandingNavbar from '../landing/sections/Navbar';
import LearnFooter from './LearnFooter';

interface Article {
  slug: string;
  title: string;
  category: string;
  reading_time: number;
  excerpt: string;
  published_at: string;
  image?: string;
}

const API_URL = import.meta.env.VITE_API_URL as string;

function ArticleGrid({ articles }: { articles: Article[] }) {
  if (articles.length === 0) {
    return (
      <div className="text-center py-12">
        <FiHeart className="mx-auto text-5xl text-base-content/20 mb-4" />
        <p className="text-base-content/60 text-lg">No articles found. Check back soon!</p>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
      {articles.map((article, index) => (
        <Link key={article.slug} to={`/learn/${article.slug}`} className="group">
          <article className="bg-base-100 rounded-2xl border border-base-200 overflow-hidden hover:shadow-lg hover:border-primary/20 transition-all duration-300 h-full flex flex-col">
            {article.image ? (
              <div className="aspect-[16/9] overflow-hidden bg-base-200">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading={index < 3 ? 'eager' : 'lazy'}
                />
              </div>
            ) : (
              <div className="aspect-[16/9] bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                <FiHeart className="text-4xl text-primary/30" />
              </div>
            )}

            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-center gap-3 mb-3">
                {article.category ? (
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary capitalize">
                    {article.category.replace(/-/g, ' ')}
                  </span>
                ) : null}
                <span className="text-xs text-(--ee-public-ink-40)">
                  {article.reading_time} min read
                </span>
              </div>

              <h2 className="text-xl font-bold text-base-content group-hover:text-primary transition-colors leading-snug">
                {article.title}
              </h2>

              <p className="mt-2 text-base-content/60 text-sm leading-relaxed flex-1">
                {article.excerpt}
              </p>

              <div className="mt-4 flex items-center text-primary text-sm font-medium gap-2">
                Read article
                <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </article>
        </Link>
      ))}
    </div>
  );
}

export default function LearnIndex() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');

  const {
    data: articles = [],
    isLoading: loading,
    isError: error,
  } = useQuery<Article[]>({
    queryKey: ['articles'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/articles`);
      const json = await r.json();
      if (!json.success) throw new Error('Failed to load articles');
      return json.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Read filters from URL
  const activeCategory = searchParams.get('category');
  const activeTag = searchParams.get('tag');
  const search = searchParams.get('search') ?? '';

  // Sync search input with URL
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const categories = Array.from(new Set(articles.map((a) => a.category))).sort();

  const filtered = articles.filter((a) => {
    const matchesCategory = !activeCategory || a.category === activeCategory;
    const matchesSearch =
      !search ||
      a.title.toLowerCase().includes(search.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(search.toLowerCase());
    // Tag matching is approximate — check title + excerpt since the list endpoint doesn't return keywords
    const matchesTag =
      !activeTag ||
      a.title.toLowerCase().includes(activeTag.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(activeTag.toLowerCase());
    return matchesCategory && matchesSearch && matchesTag;
  });

  const setCategory = (cat: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (cat) params.set('category', cat);
    else params.delete('category');
    params.delete('tag'); // clear tag when changing category
    setSearchParams(params, { replace: true });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchInput) params.set('search', searchInput);
    else params.delete('search');
    setSearchParams(params, { replace: true });
  };

  const clearSearch = () => {
    setSearchInput('');
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params, { replace: true });
  };

  const clearTag = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('tag');
    setSearchParams(params, { replace: true });
  };

  // Should we noindex? (filtered views)
  const noindex = !!search || !!activeTag;

  return (
    <div className="min-h-screen bg-base-100 flex flex-col">
      <Helmet>
        <title>Learn Nonviolent Communication - EmpathEase</title>
        <meta
          name="description"
          content="Free guides and practical tips to improve your communication, resolve conflicts, and build deeper connections using Nonviolent Communication (NVC)."
        />
        <link rel="canonical" href={`${getShareBaseUrl()}/learn`} />
        {noindex ? <meta name="robots" content="noindex, follow" /> : null}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Learn NVC - EmpathEase" />
        <meta
          property="og:description"
          content="Free guides and practical tips to improve your communication, resolve conflicts, and build deeper connections using Nonviolent Communication (NVC)."
        />
        <meta property="og:url" content={`${getShareBaseUrl()}/learn`} />
        <meta property="og:site_name" content="EmpathEase" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Learn Nonviolent Communication',
            description: 'Free guides and practical tips for Nonviolent Communication.',
            url: `${getShareBaseUrl()}/learn`,
            publisher: {
              '@type': 'Organization',
              name: 'EmpathEase',
              logo: { '@type': 'ImageObject', url: `${getShareBaseUrl()}/logo.webp` },
            },
          }).replace(/</g, '\\u003c')}
        </script>
      </Helmet>

      <LandingNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/5 via-base-100 to-secondary/5 py-16 sm:py-20 pt-32 sm:pt-36">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-base-content">
            Learn to Communicate with <span className="text-primary">Compassion</span>
          </h1>
          <p className="mt-4 text-lg sm:text-xl text-base-content/70 max-w-2xl mx-auto">
            Practical guides and exercises to help you express yourself honestly, listen deeply, and
            build stronger relationships.
          </p>

          {/* Category filter */}
          {categories.length > 0 && (
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={() => setCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  !activeCategory
                    ? 'bg-primary text-(--ee-on-accent)'
                    : 'bg-base-200 text-base-content/70 hover:bg-base-300'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors capitalize ${
                    activeCategory === cat
                      ? 'bg-primary text-(--ee-on-accent)'
                      : 'bg-base-200 text-base-content/70 hover:bg-base-300'
                  }`}
                >
                  {cat.replace(/-/g, ' ')}
                </button>
              ))}
            </div>
          )}

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-6 max-w-lg mx-auto">
            <Field
              label="Search articles"
              variant="plain"
              labelClassName="sr-only"
              controlId="article-search"
            >
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-(--ee-public-ink-30) text-sm" />
                <input
                  id="article-search"
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full pl-11 pr-4 py-3 bg-base-100 border border-base-200 rounded-xl text-base-content placeholder:text-(--ee-placeholder) focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10 transition-all text-sm"
                />
              </div>
            </Field>
          </form>
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-12 sm:py-16 flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {search ? (
            <div className="mb-8 flex items-center gap-3">
              <p className="text-base-content/60">Search results for</p>
              <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                &ldquo;{search}&rdquo;
              </span>
              <button
                onClick={clearSearch}
                className="text-sm text-(--ee-public-ink-40) hover:text-base-content transition-colors flex items-center gap-1"
              >
                <FiX /> Clear
              </button>
            </div>
          ) : null}

          {activeTag ? (
            <div className="mb-8 flex items-center gap-3">
              <p className="text-base-content/60">Showing articles tagged</p>
              <span className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                {activeTag}
              </span>
              <button
                onClick={clearTag}
                className="text-sm text-(--ee-public-ink-40) hover:text-base-content transition-colors flex items-center gap-1"
              >
                <FiX /> Clear
              </button>
            </div>
          ) : null}

          {loading ? (
            <div className="flex justify-center py-20">
              <span className="loading loading-spinner loading-lg text-primary" />
            </div>
          ) : null}

          {error ? (
            <p className="text-center text-(--ee-public-ink-50) py-20">
              Failed to load articles. Please try again later.
            </p>
          ) : null}

          {!loading && !error && activeCategory === 'conflict-resolution' && (
            <Link
              to="/conflict-resolution"
              className="mb-8 block rounded-2xl border border-primary/15 bg-gradient-to-r from-primary/5 to-transparent p-5 sm:p-6 hover:border-primary/30 transition-colors group"
            >
              <p className="text-base-content/80 text-sm sm:text-base leading-relaxed">
                <span className="font-semibold text-base-content">
                  Navigating a conflict with someone important to you?
                </span>{' '}
                A structured conversation with a neutral facilitator can help both sides feel heard.{' '}
                <span className="text-primary font-medium group-hover:underline">
                  Learn about our conflict resolution sessions &rarr;
                </span>
              </p>
            </Link>
          )}

          {!loading && !error && <ArticleGrid articles={filtered} />}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary/5 to-secondary/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold text-base-content">
            Ready to practice what you&apos;ve learned?
          </h2>
          <p className="mt-3 text-lg text-base-content/70">
            {isAuthenticated
              ? 'Continue building your communication skills with interactive courses and AI coaching.'
              : 'Take our free 3-minute quiz to discover your relationship superpower, then start building skills with interactive courses and AI coaching.'}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <Link
                to="/app"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary hover:bg-primary/90 text-(--ee-on-accent) font-semibold rounded-xl transition-colors shadow-lg shadow-primary/25 gap-2"
              >
                Go to Courses <FiArrowRight />
              </Link>
            ) : (
              <>
                <Link
                  to="/quiz-assessment"
                  className="inline-flex items-center justify-center px-6 py-3 bg-primary hover:bg-primary/90 text-(--ee-on-accent) font-semibold rounded-xl transition-colors shadow-lg shadow-primary/25"
                >
                  Take the Quiz
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-6 py-3 bg-base-100 border border-base-300 hover:border-primary/30 text-base-content font-semibold rounded-xl transition-colors gap-2"
                >
                  Start Free <FiArrowRight />
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <LearnFooter />
    </div>
  );
}
